import { CopyIcon, ExternalLinkIcon } from '@chakra-ui/icons'
import {
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useClipboard,
} from '@chakra-ui/react'
import { ScreenContext, usePopUpMessage } from '@hb/react'
import { ErrorWithMessage, getDateTimeString } from '@hb/shared'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { createGynIntakeCode } from '../../../../backend/functions/gyn'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import { ActionButton, SolidActionButton } from '../../../Buttons'
import { Subheader } from '../../Text'

const formatCountdown = (ms: number) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

const useCountdown = (expiresAt: number | null) => {
  const [remaining, setRemaining] = useState<number>(() =>
    expiresAt ? Math.max(0, expiresAt - Date.now()) : 0,
  )
  useEffect(() => {
    if (!expiresAt) return
    setRemaining(Math.max(0, expiresAt - Date.now()))
    const interval = setInterval(() => {
      setRemaining(Math.max(0, expiresAt - Date.now()))
    }, 1000)
    return () => clearInterval(interval)
  }, [expiresAt])
  return remaining
}

export const GynIntakeCodeButton = () => {
  const { userId } = useProfile()
  const { showError } = usePopUpMessage()
  const { isMobile } = useContext(ScreenContext)
  const [isGenerating, setIsGenerating] = useState(false)
  const [code, setCode] = useState<string | null>(null)
  const [expiresAt, setExpiresAt] = useState<number | null>(null)

  const intakeUrl = useMemo(() => {
    if (!code) return ''
    return `${window.location.origin}/gyn-intake-form?code=${code}`
  }, [code])

  const { onCopy: copyCode, hasCopied: codeCopied } = useClipboard(code ?? '')
  const { onCopy: copyUrl, hasCopied: urlCopied } = useClipboard(intakeUrl)

  const remaining = useCountdown(expiresAt)
  const isExpired = expiresAt !== null && remaining === 0

  const handleGenerate = useCallback(async () => {
    if (!userId) return
    try {
      setIsGenerating(true)
      const result = await createGynIntakeCode({ patientId: userId })
      setCode(result.data.code)
      setExpiresAt(result.data.expiresAt)
    } catch (error) {
      showError(
        'Error generating intake code',
        (error as ErrorWithMessage).message ?? 'Unknown error',
      )
    } finally {
      setIsGenerating(false)
    }
  }, [userId, showError])

  const hasActiveCode = code && !isExpired

  const buttonLabel = hasActiveCode
    ? `Intake Code: ${code}`
    : code && isExpired
      ? 'Code Expired — Regenerate'
      : 'Generate Intake Link'

  const triggerButton = hasActiveCode ? (
    <ActionButton size={isMobile ? 'xs' : 'sm'} gap={2}>
      <ExternalLinkIcon />
      <Text>{buttonLabel}</Text>
    </ActionButton>
  ) : (
    <SolidActionButton
      // colorScheme="blue"
      size={isMobile ? 'xs' : 'sm'}
      gap={2}
      isLoading={isGenerating || !!(hasActiveCode && !code)}
      onClick={!code ? handleGenerate : undefined}>
      <ExternalLinkIcon />
      <Text>{buttonLabel}</Text>
    </SolidActionButton>
  )

  if (!code) return triggerButton

  return (
    <Popover isLazy trigger="hover" closeOnBlur={false} strategy="fixed" placement="bottom-end">
      <PopoverTrigger>{triggerButton}</PopoverTrigger>
      <PopoverContent minW="320px">
        <PopoverBody>
          <Flex flexFlow="column" gap={3}>
            {isExpired || !expiresAt ? (
              <Text color="red.500" fontWeight={500}>
                Code expired
              </Text>
            ) : (
              <Text color="gray.600" fontSize="sm">
                Expires in <strong>{formatCountdown(remaining)}</strong> (at{' '}
                {getDateTimeString(expiresAt, 'short')})
              </Text>
            )}

            <Flex flexFlow="column" gap={1}>
              <Subheader fontWeight={500}>Access Code</Subheader>
              <InputGroup size="sm">
                <Input
                  isReadOnly
                  value={code}
                  fontFamily="mono"
                  letterSpacing="widest"
                  fontWeight={700}
                  fontSize="lg"
                />
                <InputRightElement>
                  <IconButton
                    aria-label="Copy code"
                    icon={<CopyIcon />}
                    size="xs"
                    onClick={copyCode}
                    colorScheme={codeCopied ? 'green' : 'gray'}
                  />
                </InputRightElement>
              </InputGroup>
            </Flex>

            <Flex flexFlow="column" gap={1}>
              <Subheader fontWeight={500}>Intake Form URL</Subheader>
              <InputGroup size="sm">
                <Input isReadOnly value={intakeUrl} fontSize="xs" />
                <InputRightElement>
                  <IconButton
                    aria-label="Copy URL"
                    icon={<CopyIcon />}
                    size="xs"
                    onClick={copyUrl}
                    colorScheme={urlCopied ? 'green' : 'gray'}
                  />
                </InputRightElement>
              </InputGroup>
            </Flex>

            <ActionButton size="sm" isLoading={isGenerating} onClick={handleGenerate}>
              Regenerate Code
            </ActionButton>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}
